import { Alert, Box, Button, CircularProgress, Paper, TextField, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import { provisionInstanceFromAdminPanel } from "../../../actions/provisioningActions";
import store from "../../../store";
import "./Provisioning.scss";

const EMPTY_FORM = {
    email: "",
    companyName: "",
    firstName: "",
    lastName: "",
    phoneNumber: "",
};

/**
 * Admin Panel page to start website-registration-style instance provisioning.
 *
 * @returns {JSX.Element} Provisioning form
 */
function Provisioning() {
    const [form, setForm] = useState(EMPTY_FORM);
    const [submitting, setSubmitting] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    const handleChange = useCallback((event) => {
        const { name, value } = event.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    }, []);

    const handleSubmit = useCallback(
        async (event) => {
            event.preventDefault();
            setSuccessMessage("");
            setErrorMessage("");

            const email = form.email.trim();
            if (!email) {
                setErrorMessage("E-Mail ist erforderlich.");
                return;
            }

            const payload = {
                email,
                ...(form.companyName.trim() ? { companyName: form.companyName.trim() } : {}),
                ...(form.firstName.trim() ? { firstName: form.firstName.trim() } : {}),
                ...(form.lastName.trim() ? { lastName: form.lastName.trim() } : {}),
                ...(form.phoneNumber.trim() ? { phoneNumber: form.phoneNumber.trim() } : {}),
            };

            setSubmitting(true);
            try {
                const result = await store.dispatch(provisionInstanceFromAdminPanel(payload));
                if (result?.status === "PROVISIONING") {
                    setSuccessMessage(
                        `Provisionierung gestartet (Status: PROVISIONING${
                            result.websiteRegistrationId != null
                                ? `, Registrierung #${result.websiteRegistrationId}`
                                : ""
                        }).`
                    );
                    setForm(EMPTY_FORM);
                } else {
                    setErrorMessage("Unerwartete Antwort vom Server.");
                }
            } catch (error) {
                const details =
                    error?.body?.details && Array.isArray(error.body.details)
                        ? `: ${error.body.details.join(", ")}`
                        : "";
                setErrorMessage((error?.message || "Provisionierung fehlgeschlagen") + details);
            } finally {
                setSubmitting(false);
            }
        },
        [form]
    );

    return (
        <div className="provisioning" id="provisioning-page">
            <Typography variant="h4" component="h1" sx={{ mb: 2 }}>
                Instanz provisionieren
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
                Das Erstellen kann einige Minuten dauern. <br />
                <b>
                    ACHTUNG: <br />
                    Bei Fertigstellung oder im Fehlerfall wird sofort eine E-Mail an die angegebene E-Mail-Adresse
                    gesendet. <br />
                    Falls das nicht gewünscht ist, bitte eine interne E-Mail-Adresse verwenden.
                </b>
            </Typography>

            {successMessage ? (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage("")}>
                    {successMessage}
                </Alert>
            ) : null}
            {errorMessage ? (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage("")}>
                    {errorMessage}
                </Alert>
            ) : null}

            <Paper component="form" onSubmit={handleSubmit} sx={{ p: 3, maxWidth: 560 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <TextField
                        id="provisioning-email"
                        name="email"
                        label="E-Mail"
                        type="email"
                        value={form.email}
                        onChange={handleChange}
                        required
                        fullWidth
                        autoComplete="email"
                    />
                    <TextField
                        id="provisioning-company-name"
                        name="companyName"
                        label="Unternehmensname"
                        value={form.companyName}
                        onChange={handleChange}
                        fullWidth
                    />
                    <TextField
                        id="provisioning-first-name"
                        name="firstName"
                        label="Vorname"
                        value={form.firstName}
                        onChange={handleChange}
                        fullWidth
                    />
                    <TextField
                        id="provisioning-last-name"
                        name="lastName"
                        label="Nachname"
                        value={form.lastName}
                        onChange={handleChange}
                        fullWidth
                    />
                    <TextField
                        id="provisioning-phone-number"
                        name="phoneNumber"
                        label="Telefonnummer"
                        value={form.phoneNumber}
                        onChange={handleChange}
                        fullWidth
                    />
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <Button
                            id="provisioning-submit"
                            type="submit"
                            variant="contained"
                            disabled={submitting}
                            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
                        >
                            Provisionierung starten
                        </Button>
                    </Box>
                </Box>
            </Paper>
        </div>
    );
}

export default Provisioning;
